import "./TreeNode.less";
import { useState, ReactElement, useEffect } from "react";
import { GuxIconIconName } from "genesys-spark-components";
import { GuxIcon } from "genesys-spark-components-react";

//Tree node contains both MetaData and Children Nodes
//MetaData Handles what the TreeNode will look like and its style,
//Children Nodes will be of type TreeNode
interface TreeNodeProps {
    treeNodeCustomContent: any;
    treeNodeCustomIcon?: any;
    hasCustomIcon: boolean;
    index: string;
    iconDataOpen?: GuxIconIconName;
    iconDataClosed?: GuxIconIconName;
    children?:
        | ReactElement<typeof TreeNode>
        | Array<ReactElement<typeof TreeNode>>;
    hasError?: boolean;
}

const defaultIconOpen = "arrow-solid-down";
const defaultIconClosed = "arrow-solid-up";

//MetaData is completely customizeable by the user both in content and style
export function TreeNodeCustomContent({ children }: { children: any }) {
    return <div className="TreeNodeCustomContent">{children}</div>;
}

export function TreeNodeCustomIcon({ children }: { children: any }) {
    return (
        <div className="TreeNodeCustomIcon" data-testid="tree-node-custom-icon">
            {children}
        </div>
    );
}

export function TreeNode({
    treeNodeCustomContent,
    treeNodeCustomIcon,
    hasCustomIcon: hasQuestionDetailsViewIcon,
    iconDataOpen,
    iconDataClosed,
    children,
    index,
    hasError = false,
}: TreeNodeProps) {
    const [showChildren, setShowChildren] = useState(true);
    const icon = (
        <GuxIcon
            className="expander"
            iconName={showChildren ? iconDataOpen : iconDataClosed}
            decorative={true}
        ></GuxIcon>
    );
    const [isMouseOverRoot, setIsMouseOverRoot] = useState(false);
    const [isRootFocused, setIsRootFocused] = useState(false);
    const [rootBGColourClass, setRootBGColourClass] = useState("");

    useEffect(() => {
        if (isMouseOverRoot || isRootFocused) {
            setRootBGColourClass("root-hovered");
        } else {
            setRootBGColourClass("");
        }
    }, [isMouseOverRoot, isRootFocused]);

    return (
        <div className="TreeNode" role="treeitem" aria-selected={undefined}>
            <div className="tree-node-row">
                <div className="gux-label">{index}</div>
                <div
                    className={`tree-node-content ${hasError ? "error" : ""} ${
                        children ? `Clickable ${rootBGColourClass}` : ""
                    }`}
                    onMouseEnter={() => setIsMouseOverRoot(true)}
                    onMouseLeave={() => setIsMouseOverRoot(false)}
                    onFocus={() => setIsRootFocused(true)}
                    onBlur={() => setIsRootFocused(false)}
                    onClick={() => {
                        setShowChildren(!showChildren);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            setShowChildren(!showChildren);
                        }
                    }}
                    tabIndex={children === undefined ? -1 : 0}
                    role={children ? "button" : "none"}
                    aria-expanded={children ? showChildren : undefined}
                >
                    <TreeNodeCustomContent
                        children={treeNodeCustomContent}
                    ></TreeNodeCustomContent>
                    {!(children === undefined) && icon}
                    {/* <div className="icon-class">
                    </div> */}
                </div>
                {hasQuestionDetailsViewIcon && (
                    <div className={`tree-node-icon`}>
                        {treeNodeCustomIcon ? (
                            <>
                                <TreeNodeCustomIcon
                                    children={treeNodeCustomIcon}
                                ></TreeNodeCustomIcon>
                            </>
                        ) : (
                            <div className="tree-node-icon-space"></div>
                        )}
                    </div>
                )}
            </div>
            <div
                className="tree-node-child"
                role="group"
                children={showChildren && children}
            ></div>
        </div>
    );
}

TreeNode.defaultProps = {
    iconDataOpen: defaultIconOpen,
    iconDataClosed: defaultIconClosed,
};
